import { zodResolver } from '@hookform/resolvers/zod';
import {
  CollectPaymentForm as CollectPaymentFormType,
  collectPaymentFormSchema,
} from '@shared/types';
import { useForm } from 'react-hook-form';
import { CollectPaymentForm } from './collect-payment-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@web/components/ui/dialog';
import { useEffect, useState } from 'react';
import { trpc } from '@web/lib/trpc';

const defaultValues = {
  payment: {
    amount: 0,
    currency: 'USD',
  },

  billing: {
    firstName: '',
    lastName: '',
    email: '',
    address1: '',
    zip: '',
  },

  card: {
    number: '',
    cvv: '',
    exp: '',
  },
};

export function PaymentCollector({
  contactId,
  contractId,
  onClose,
}: {
  contactId: string;
  contractId: string;
  onClose?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(true);

  const { data: contactBasicData } = trpc.contact.getContactBasicInfo.useQuery({
    contactId,
  });

  const { data: contactDetailsData } = trpc.contact.getContactDetails.useQuery({
    contactId,
  });

  const { mutateAsync: collectAsync } = trpc.payment.collect.useMutation();

  const form = useForm<CollectPaymentFormType>({
    resolver: zodResolver(collectPaymentFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (contactBasicData)
      form.reset({
        ...defaultValues,
        billing: {
          ...defaultValues.billing,
          firstName: contactBasicData.firstName ?? '',
          lastName: contactBasicData.lastName ?? '',
          email: contactBasicData.email ?? '',
        },
      });
  }, [contactBasicData, form]);

  useEffect(() => {
    if (contactDetailsData)
      form.reset({
        ...defaultValues,
        billing: {
          address1: contactDetailsData.address ?? '',
          zip: contactDetailsData.zipCode ?? '',
        },
      });
  }, [contactDetailsData, form]);

  const handleSubmit = async (values: CollectPaymentFormType) => {
    await collectAsync({
      ...values,
      card: {
        number: values.card.number.replaceAll(' ', ''),
        exp: values.card.exp.replaceAll(' ', ''),
        cvv: values.card.cvv,
      },
      contractId,
    });
    setIsOpen(false);
    onClose?.();
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Collect Payment</DialogTitle>
          <DialogDescription>Payment details</DialogDescription>
        </DialogHeader>

        <CollectPaymentForm form={form} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
}
